import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import AppHeader from '../components/AppHeader';
import Icon from '../components/Icon';
import EmailVerifyModal from '../components/EmailVerifyModal';
import usePublicSettings from '../hooks/usePublicSettings';

const PLAN_ORDER = ['monthly', 'quarterly', 'biannual'];
const MAX_QTY = 20;

export default function Checkout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, hasActiveLicense, reloadServers } = useAuth();
  const needsVerify = user && !user.emailVerified;
  const settings = usePublicSettings();
  const PLAN_PRICES = settings.pricing;
  const extraDiscPct = Number(settings.plans?.extraServerDiscountPct ?? settings.pricing?.extraServerDiscountPct ?? 35);
  const MONTHLY_EQUIV = {
    monthly: PLAN_PRICES.monthly,
    quarterly: PLAN_PRICES.quarterly / 3,
    biannual: PLAN_PRICES.biannual / 6,
  };

  // ?add=N  → mevcut aboneliğe sunucu ekleme modu (gün-oranlı)
  const addModeRaw = parseInt(params.get('add'), 10);
  const addMode = hasActiveLicense && addModeRaw >= 1;

  const [plan, setPlan] = useState('quarterly');
  const [qty, setQty] = useState(1);
  const [addCount, setAddCount] = useState(Number.isFinite(addModeRaw) && addModeRaw >= 1 ? addModeRaw : 1);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponMsg, setCouponMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pay, setPay] = useState(null);
  const [addQuote, setAddQuote] = useState(null);
  const [showVerify, setShowVerify] = useState(false);

  const discount = coupon ? coupon.discountPercent : 0;
  const base = PLAN_PRICES[plan];
  const perExtra = base * (1 - extraDiscPct / 100);
  const subtotal = base + (qty - 1) * perExtra;
  const total = useMemo(() => (subtotal * (1 - discount / 100)).toFixed(2), [subtotal, discount]);
  const isFree = Number(total) < 1;

  // add-server tutar önizleme
  useEffect(() => {
    if (!addMode) return undefined;
    let alive = true;
    api.addServerQuote(addCount).then((q) => { if (alive) setAddQuote(q); }).catch(() => {});
    return () => { alive = false; };
  }, [addMode, addCount]);

  const applyCoupon = async () => {
    setCouponMsg('');
    if (!couponInput.trim()) { setCoupon(null); return; }
    try {
      const res = await api.validateCoupon(couponInput.trim());
      if (res.valid) {
        setCoupon({ code: res.code, discountPercent: res.discountPercent });
        setCouponMsg(t('checkout.coupon_applied', { pct: res.discountPercent }));
      } else { setCoupon(null); setCouponMsg(t('checkout.coupon_invalid')); }
    } catch (_) { setCoupon(null); setCouponMsg(t('checkout.coupon_invalid')); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (needsVerify) { setShowVerify(true); return; }
    setError('');
    setBusy(true);
    try {
      const res = addMode
        ? await api.addServer(addCount)
        : await api.checkout({ plan, couponCode: coupon ? coupon.code : undefined, serverSlots: qty });
      if (res.free) {
        await reloadServers();
        navigate(addMode ? '/dashboard/servers' : `/checkout/success?oid=${encodeURIComponent(res.merchantOid)}`);
        return;
      }
      setPay({ iframeUrl: res.iframeUrl, bridgeOrigin: res.bridgeOrigin || null, merchantOid: res.merchantOid });
    } catch (err) {
      setBusy(false);
      if (err.code === 'email_not_verified') setShowVerify(true);
      else if (err.code === 'paytr_not_configured') setError(t('checkout.not_configured'));
      else if (err.code === 'invalid_coupon') setError(t('checkout.coupon_invalid'));
      else setError(err.message || t('checkout.failed_desc'));
    }
  };

  useEffect(() => {
    if (!pay || !pay.bridgeOrigin) return undefined;
    const onMsg = (ev) => {
      if (ev.origin !== pay.bridgeOrigin) return;
      const d = ev.data || {};
      if (d.source !== 'vokelle-pay') return;
      const oid = d.oid || pay.merchantOid;
      if (addMode) { reloadServers().then(() => navigate('/dashboard/servers')); return; }
      navigate(`/checkout/${d.status === 'success' ? 'success' : 'failed'}?oid=${encodeURIComponent(oid)}`);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [pay, navigate, addMode, reloadServers]);

  useEffect(() => { if (needsVerify) setShowVerify(true); }, [needsVerify]);

  useEffect(() => {
    if (!pay || pay.bridgeOrigin) return undefined;
    const s = document.createElement('script');
    s.src = 'https://www.paytr.com/js/iframeResizer.min.js';
    s.async = true;
    s.onload = () => { try { window.iFrameResize({}, '#paytriframe'); } catch (_) {} };
    document.body.appendChild(s);
    return () => { try { document.body.removeChild(s); } catch (_) {} };
  }, [pay]);

  useEffect(() => { document.title = 'Vokelle — ' + t('checkout.title'); }, [t]);

  const addTotal = addQuote ? addQuote.totalStr : null;
  const addIsFree = addTotal != null && Number(addTotal) < 1;

  const Stepper = ({ value, set, min = 1, max = MAX_QTY }) => (
    <div className="qty-stepper">
      <button type="button" onClick={() => set(Math.max(min, value - 1))} disabled={value <= min}>−</button>
      <span>{value}</span>
      <button type="button" onClick={() => set(Math.min(max, value + 1))} disabled={value >= max}>+</button>
    </div>
  );

  return (
    <>
      <AppHeader />
      {showVerify && needsVerify && <EmailVerifyModal onVerified={() => setShowVerify(false)} />}
      <div className="container" style={{ maxWidth: 860, padding: '48px 20px 80px' }}>
        <span className="eyebrow">{addMode ? t('checkout.add_eyebrow') : t('checkout.title')}</span>
        <h1 style={{ fontSize: '2rem', margin: '12px 0 8px' }}>{addMode ? t('checkout.add_title') : t('checkout.title')}</h1>
        <p style={{ color: 'var(--text-2)', marginBottom: 32 }}>{addMode ? t('checkout.add_subtitle') : t('checkout.subtitle')}</p>

        {hasActiveLicense && !addMode && (
          <div className="badge info" style={{ marginBottom: 20 }}>
            <Icon name="check" size={13} /> {t('checkout.already_active')}
          </div>
        )}

        {/* ---------- SUNUCU EKLEME MODU ---------- */}
        {addMode ? (
          !pay ? (
            <form className="card" onSubmit={submit}>
              <h3 style={{ fontSize: '1rem', marginBottom: 6 }}>{t('checkout.add_how_many')}</h3>
              <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 16 }}>{t('checkout.add_prorate_note')}</p>
              <Stepper value={addCount} set={setAddCount} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '14px 0', borderTop: '1px solid var(--border)', marginTop: 16 }}>
                <span style={{ color: 'var(--text-2)' }}>
                  {t('checkout.total')}
                  {addQuote && <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 8 }}>{t('checkout.add_days_left', { n: addQuote.remainingDays })}</span>}
                </span>
                <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '1.5rem', fontWeight: 600 }}>
                  {addTotal != null ? `₺${addTotal}` : '…'}
                </span>
              </div>

              {error && <div className="form-error"><Icon name="alert-triangle" size={14} /> {error}</div>}
              <button className="btn btn-block btn-lg" disabled={busy || addTotal == null} style={{ marginTop: 4 }}>
                {busy ? t('checkout.processing')
                  : addIsFree ? t('checkout.add_cta_free', { n: addCount })
                  : `${t('checkout.add_cta', { n: addCount })} — ₺${addTotal}`}
              </button>
            </form>
          ) : null
        ) : (
          <>
            {/* ---------- YENİ SATIN ALMA ---------- */}
            <div className="pricing-grid" style={{ marginBottom: 24, opacity: pay ? 0.55 : 1, pointerEvents: pay ? 'none' : 'auto' }}>
              {PLAN_ORDER.map((p) => {
                const selected = plan === p;
                const savePct = Math.round((1 - MONTHLY_EQUIV[p] / PLAN_PRICES.monthly) * 100);
                return (
                  <button type="button" key={p} className={`card price-card ${selected ? 'featured' : ''}`}
                    style={{ cursor: 'pointer', textAlign: 'center' }} onClick={() => setPlan(p)}>
                    <div className="plan-name">{t(`checkout.plan_${p}`)}</div>
                    <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '1.9rem', fontWeight: 600, margin: '6px 0' }}>
                      ₺{Math.round(MONTHLY_EQUIV[p])}
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-3)', fontWeight: 400 }}> {t('checkout.per_month')}</span>
                    </div>
                    <div className="plan-hint">{t(`checkout.billed_${p}`)}</div>
                    {savePct > 0 && <div className="badge ok" style={{ marginTop: 6 }}>{t('checkout.save_pct', { pct: savePct })}</div>}
                  </button>
                );
              })}
            </div>

            {!pay && (
              <div className="card" style={{ marginBottom: 20 }}>
                <div className="field" style={{ marginBottom: 18 }}>
                  <label>{t('checkout.servers_label')}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <Stepper value={qty} set={setQty} />
                    <span style={{ color: 'var(--text-3)', fontSize: 13 }}>
                      {qty > 1
                        ? t('checkout.servers_hint_multi', { pct: extraDiscPct, price: Math.round(perExtra) })
                        : t('checkout.servers_hint_one')}
                    </span>
                  </div>
                </div>

                <div className="field">
                  <label>{t('checkout.coupon')}</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="VOK-…" style={{ flex: 1 }} />
                    <button type="button" className="btn btn-subtle btn-sm" onClick={applyCoupon}>{t('checkout.coupon_apply')}</button>
                  </div>
                  {couponMsg && <div className="hint" style={{ color: coupon ? 'var(--ok)' : 'var(--crit)' }}>{couponMsg}</div>}
                </div>

                {qty > 1 && (
                  <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '10px 0 0', borderTop: '1px solid var(--border)', marginTop: 12 }}>
                    {t('checkout.breakdown', { base: base.toFixed(0), extras: qty - 1, perExtra: Math.round(perExtra) })}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '12px 0 0', marginTop: qty > 1 ? 4 : 8, borderTop: qty > 1 ? 0 : '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-2)' }}>{t('checkout.total')}</span>
                  <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '1.5rem', fontWeight: 600 }}>
                    ₺{total}
                    {discount > 0 && <span style={{ fontSize: '0.85rem', color: 'var(--text-3)', textDecoration: 'line-through', marginLeft: 8 }}>₺{subtotal.toFixed(2)}</span>}
                  </span>
                </div>
              </div>
            )}

            {!pay && (
              <form className="card" onSubmit={submit}>
                <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>{isFree ? t('checkout.free_title') : t('checkout.card_title')}</h3>
                <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 16 }}>
                  {isFree ? t('checkout.free_intro') : t('checkout.iframe_intro')}
                </p>
                {error && <div className="form-error"><Icon name="alert-triangle" size={14} /> {error}</div>}
                <button className="btn btn-block btn-lg" disabled={busy} style={{ marginTop: 4 }}>
                  {busy ? t('checkout.processing') : isFree ? t('checkout.free_cta') : `${t('checkout.pay')} — ₺${total}`}
                </button>
                {!isFree && (
                  <p className="hint" style={{ marginTop: 10, textAlign: 'center' }}>
                    <Icon name="shield" size={12} /> {t('checkout.pay_hint')}
                  </p>
                )}
              </form>
            )}
          </>
        )}

        {pay && (
          <div className="card" style={{ padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, padding: '0 4px' }}>
              <span style={{ color: 'var(--text-2)', fontSize: 13 }}>
                <Icon name="shield" size={12} /> {t('checkout.iframe_secure')}
              </span>
              <button type="button" className="btn btn-subtle btn-sm" onClick={() => { setPay(null); setBusy(false); }}>
                {t('checkout.iframe_back')}
              </button>
            </div>
            <iframe title="Güvenli ödeme" src={pay.iframeUrl} id="paytriframe" frameBorder="0"
              scrolling={pay.bridgeOrigin ? 'auto' : 'no'} style={{ width: '100%', minHeight: 580, border: 0 }} />
          </div>
        )}
      </div>
    </>
  );
}
